'use client';

import { useMemo } from 'react';

import { useModal } from '@/components/ModalContext';
import { useProduct, getVariantBasedOnSelectedOptions } from '@/contexts/ProductContext';
import { usePricing } from '@/contexts/PricingContext';
import { formatPrice, toCents, toDollars } from '@/utils/utilities.mjs';
import { getShopifyOptionByName } from '@/components/blocks/variantOptions/shopifyOptionConventions';
import { getSelectedOptionValue, optionNamesMatch, optionValuesMatch } from '@/utils/product/product-context-helpers';
import VariantButtonsModal from './VariantButtonsModal';

// ─── Local pricing helpers (single consumer — no need to extract) ─────────────

/** Cheapest variant in the slice that matches all current selections except `optionName`. */
function getBaseVariant(variants, selectedVariant, optionName, pricing) {
  if (!variants?.length || !selectedVariant) return null;
  const slice = variants.filter((v) =>
    v.selectedOptions.every((o) =>
      optionNamesMatch(o.name, optionName)
        ? true
        : selectedVariant.selectedOptions.some(
            (s) => optionNamesMatch(s.name, o.name) && s.value === o.value,
          ),
    ),
  );
  if (!slice.length) return null;
  return slice.reduce((a, b) =>
    pricing.variant(a).sale <= pricing.variant(b).sale ? a : b,
  );
}

/** Raw sale and full-price difference between two variants (negative = cheaper). */
function getPriceDiff(variantA, variantB, pricing) {
  if (!variantA || !variantB) return { sale: undefined, full: undefined, hasDiff: false };
  const a = pricing.variant(variantA);
  const b = pricing.variant(variantB);
  // sale is null when no pricing strategy is configured, toCents(null) would otherwise read as 0.
  if (a.sale == null || b.sale == null) return { sale: undefined, full: undefined, hasDiff: false };
  const saleCents = toCents(a.sale) - toCents(b.sale);
  const fullCents = toCents(a.full) - toCents(b.full);
  return { sale: toDollars(saleCents), full: toDollars(fullCents), hasDiff: saleCents !== fullCents };
}

// ─── Local sub-component ──────────────────────────────────────────────────────

function PriceLabel({ sale, full, hasDiff }) {
  if (sale === undefined) return null;
  const prefix = sale < 0 ? '' : '+';
  return (
    <div className="flex flex-col text-right tabular-nums font-worksans">
      <span className="text-sm font-medium text-primary">{prefix}{formatPrice(sale)}</span>
      {hasDiff && full > 0 && (
        <span className="text-xs line-through text-primary/50">{prefix}{formatPrice(full)}</span>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

const VariantButtons = ({ block, resolvedOptionName }) => {
  const { selectedVariant, updateOptions, productData } = useProduct();
  const pricing = usePricing();
  const { openModal } = useModal();

  const {
    variant_options: rows = [],
    heading = '',
    short_description: shortDescription = '',
    modal,
  } = block ?? {};

  const shopifyProduct = productData?.shopifyProduct;
  const shopifyOption = getShopifyOptionByName(shopifyProduct, resolvedOptionName);

  const currentValue = getSelectedOptionValue(selectedVariant, resolvedOptionName);

  const baseVariant = useMemo(() => getBaseVariant(
    shopifyProduct?.variants,
    selectedVariant,
    resolvedOptionName,
    pricing,
  ), [shopifyProduct?.variants, selectedVariant, resolvedOptionName, pricing]);

  const renderedRows = useMemo(() => {
    if (!shopifyOption) return [];
    return rows.reduce((acc, { title, description = '' }) => {
      // Skip CMS rows for values Shopify no longer has (e.g. a removed color).
      const optionValue = shopifyOption.values.find((v) => optionValuesMatch(v, title));
      if (!optionValue) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[VariantButtons] No Shopify match for CMS title: "${title}".`);
        }
        return acc;
      }

      const isSelected = currentValue === optionValue;

      const matchingVariant = getVariantBasedOnSelectedOptions(
        resolvedOptionName,
        optionValue,
        productData,
        selectedVariant,
      );

      const { sale, full, hasDiff } = getPriceDiff(matchingVariant, baseVariant, pricing);

      acc.push({
        title,
        description,
        optionValue,
        isSelected,
        sale,
        full,
        hasDiff,
      });
      return acc;
    }, []);
  }, [
    rows,
    shopifyOption,
    productData,
    selectedVariant,
    resolvedOptionName,
    pricing,
    baseVariant,
    currentValue,
  ]);

  if (!shopifyOption || !renderedRows.length || !selectedVariant) return null;

  return (
    <section className="flex flex-col gap-4 font-worksans text-primary" data-block="variant_buttons">
      <div className="flex items-start justify-between gap-3 sm:items-center">
        {heading ? <p className="text-base font-semibold leading-5">{heading}</p> : null}
        {modal?.label && modal?.modal_content ? (
          <button
            type="button"
            className="shrink-0 cursor-pointer text-right text-sm leading-5 text-sea underline"
            onClick={() => openModal(<VariantButtonsModal modalDoc={modal.modal_content} />, null, 'bg-white')}
          >
            {modal.label}
          </button>
        ) : null}
      </div>

      {shortDescription ? (
        <p className="text-base leading-6">{shortDescription}</p>
      ) : null}

      <fieldset className="grid gap-2.5 border-0 p-0">
        <legend className="sr-only">{heading || resolvedOptionName}</legend>
        {renderedRows.map(({ title, description, optionValue, isSelected, sale, full, hasDiff }) => {
          return (
            <label
              key={optionValue}
              className={`relative flex cursor-pointer bg-white p-5 outline-none transition-colors duration-150 border-2 has-focus-visible:ring-2 has-focus-visible:ring-sea has-focus-visible:ring-offset-2 ${
                isSelected
                  ? 'border-sea'
                  : 'border-sand-light hover:border-sand hover:bg-sand'
              }`}
            >
              <input
                type="radio"
                name={`variant-${resolvedOptionName}`}
                value={optionValue}
                checked={isSelected}
                onChange={() =>
                  updateOptions([{ name: resolvedOptionName, value: optionValue }], { persist: true })
                }
                className="sr-only"
              />
              <div className="flex w-full justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-base font-semibold leading-5">{title}</div>
                  {description ? (
                    <div className="pt-1.5 text-sm font-normal leading-5 text-primary/70">
                      {description}
                    </div>
                  ) : null}
                </div>
                <PriceLabel sale={sale} full={full} hasDiff={hasDiff} />
              </div>
            </label>
          );
        })}
      </fieldset>
    </section>
  );
};

export default VariantButtons;
